import React, { useState, useEffect } from "react";
import "../styles/MyProfile.css";
import Lottie from "lottie-react";
import { FaUserCheck } from "react-icons/fa";
import alreadyAnimation from "../assets/Already.json";
import doneAnimation from "../assets/Done.json";
import { useLocation } from "react-router-dom";
import { getJson, postJson } from "../network/api";
import { ApiEndpoints } from "../network/config";

const calculateAge = (dobString) => {
  if (!dobString) return "—";

  // Convert DOB into a valid Date object
  const dob = new Date(dobString);

  if (isNaN(dob.getTime())) return "—"; // invalid date handling

  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();

  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  // Deduct age by 1 if birthday hasn't come yet this year
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};

const display = (value) => {
  if (!value && value !== 0) return "—";
  return value;
};

const ProfileInfo = () => {
  const location = useLocation();

  // Extract profileId from navigation state
  const routedProfile = location.state?.profile;
  const profileId =
    location.state?.profileId ||
    routedProfile?.id ||
    routedProfile?.profileId ||
    location.state?.id;

  const userId = sessionStorage.getItem("userId");

  const [activeTab, setActiveTab] = useState("basic");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [profileData, setProfileData] = useState(null);

  // Modal control
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(""); // confirm | success | already

  // Auto-close modal (success or already)
  useEffect(() => {
    if (modalType === "success" || modalType === "already") {
      const timer = setTimeout(() => setShowModal(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [modalType]);

  // Fetch profile details
  useEffect(() => {
    if (!profileId) return;

    const loadProfileInfo = async () => {
      try {
        const response = await getJson(
          `${ApiEndpoints.findByProfileId}/${profileId}`
        );
        setProfileData(response.data);
      } catch (error) {
        console.error("Error loading profile info:", error);
      }
    };

    loadProfileInfo();
  }, [profileId]);

  if (!profileData) {
    return (
      <h3 style={{ textAlign: "center", marginTop: "90px" }}>Loading...</h3>
    );
  }

  // IMAGE SLIDER LIST
  const images = profileData.profileImages?.map((img) => img.imageUrl) || [];

  const nextImage = () => {
    setCurrentImageIndex((i) => (i + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  };

  // HANDLE PROFILE SELECTION
  const handleSelectProfile = async () => {
    try {
      // 1️⃣ Check if profile already selected
      const selectedRes = await getJson(ApiEndpoints.selectedProfiles, {
        params: {
          page: 1,
          limit: 9999,
          appUserId: userId,
        },
      });

      const selectedList = selectedRes?.data || [];

      const alreadySelected = selectedList.some(
        (p) => p.id === profileId || p.profileId === profileId
      );

      if (alreadySelected) {
        setModalType("already");
        setShowModal(true);
        return;
      }

      // 2️⃣ Send select profile API request
      await postJson(
        ApiEndpoints.selectProfile,
        {},
        {
          params: {
            userId,
            profileId,
          },
        }
      );

      // 3️⃣ Show success modal
      setModalType("success");
      setShowModal(true);
    } catch (error) {
      console.error("Select Profile Error:", error);

      // fallback → show already-selected modal
      setModalType("already");
      setShowModal(true);
    }
  };

  return (
    <div className="my-profile-container">
      <h1 className="my-profile-title">All Profile</h1>

      <div className="my-profile-content">
        {/* LEFT — IMAGE + SELECT BUTTON */}
        <div className="my-profile-left">
          <button
            className="select-profile-btn"
            onClick={() => {
              setModalType("confirm");
              setShowModal(true);
            }}
          >
            <FaUserCheck style={{ marginRight: "8px", fontSize: "16px" }} />
            Select Profile
          </button>

          <div className="my-image-slider">
            {images.length > 0 ? (
              <img
                src={images[currentImageIndex]}
                alt="Profile"
                className="my-profile-image"
                onError={(e) => (e.target.style.display = "none")}
              />
            ) : (
              <div className="no-image-box">No Image Available</div>
            )}

            {images.length > 1 && (
              <>
                <button
                  className="my-slider-btn my-prev-btn"
                  onClick={prevImage}
                >
                  ‹
                </button>
                <button
                  className="my-slider-btn my-next-btn"
                  onClick={nextImage}
                >
                  ›
                </button>
              </>
            )}

            {images.length > 1 && (
              <div className="my-image-indicators">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`my-indicator ${
                      index === currentImageIndex ? "my-active-indicator" : ""
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ------- MODAL POPUPS -------- */}
          {showModal && (
            <div className="modal-overlay">
              <div className="modal">
                {/* Confirm Modal */}
                {modalType === "confirm" && (
                  <div className="modal-content">
                    <p className="modal-message">
                      Are you sure you want to select this profile?
                    </p>

                    <div className="modal-buttons">
                      <button className="btn-yes" onClick={handleSelectProfile}>
                        Yes
                      </button>
                      <button
                        className="btn-no"
                        onClick={() => setShowModal(false)}
                      >
                        No
                      </button>
                    </div>
                  </div>
                )}
                {/* Already Selected */}
                {modalType === "already" && (
                  <div style={{ textAlign: "center" }}>
                    <Lottie
                      animationData={alreadyAnimation}
                      loop={false}
                      style={{ width: 120, height: 120, margin: "0 auto" }}
                    />
                    <p>This profile is already selected.</p>
                  </div>
                )}
                {/* Success */}
                {modalType === "success" && (
                  <div style={{ textAlign: "center" }}>
                    <Lottie
                      animationData={doneAnimation}
                      loop={false}
                      style={{ width: 120, height: 120, margin: "0 auto" }}
                    />
                    <p>Profile selected successfully!</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ---------------- RIGHT — DETAILS SECTION ---------------- */}
        <div className="my-profile-right">
          <div className="my-tabs">
            {[
              "basic",
              "personal",
              "birth",
              "family",
              "contact",
              "education",
              "looking",
            ].map((tab) => (
              <button
                key={tab}
                className={`my-tab ${activeTab === tab ? "my-active-tab" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "looking"
                  ? "Looking For"
                  : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* BASIC DETAILS */}
          {activeTab === "basic" && (
            <>
              <h3>Basic Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Gender</span>
                  <span>{display(profileData.gender)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Age</span>
                  <span>{calculateAge(profileData.birthDetails?.Dob)}</span>
                </div>
                <div className="my-detail-row">
                  <span>SGS No</span>
                  <span>{display(profileData.sgsNo)}</span>
                </div>
                <div className="my-detail-row">
                  <span>File No</span>
                  <span>{display(profileData.fileNo)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Branch</span>
                  <span>{display(profileData.branchDetails?.branch_name)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Caste</span>
                  <span>{display(profileData.caste)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Subcaste</span>
                  <span>{display(profileData.subcaste)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Bedagu</span>
                  <span>{display(profileData.bedagu)}</span>
                </div>
              </div>
            </>
          )}

          {/* PERSONAL DETAILS */}
          {activeTab === "personal" && (
            <>
              <h3>Personal Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Height</span>
                  <span>{display(profileData.personalDetails?.height)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Weight</span>
                  <span>{display(profileData.personalDetails?.weight)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Blood Group</span>
                  <span>
                    {display(profileData.personalDetails?.bloodGroup)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Body Type</span>
                  <span>{display(profileData.personalDetails?.bodyType)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Complexion</span>
                  <span>
                    {display(profileData.personalDetails?.complexion)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Marital Status</span>
                  <span>
                    {display(profileData.personalDetails?.maritalStatus)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Interested In Foreign Visits</span>
                  <span>
                    {display(
                      profileData.personalDetails?.interestedInForeignVisits
                    )}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* BIRTH DETAILS */}
          {activeTab === "birth" && (
            <>
              <h3>Birth Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Date of Birth</span>
                  <span>{display(profileData.birthDetails?.Dob)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Birth Place</span>
                  <span>{display(profileData.birthDetails?.birthPlace)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Birth Time</span>
                  <span>{display(profileData.birthDetails?.birthTime)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Birth Day</span>
                  <span>{display(profileData.birthDetails?.birthDay)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Star</span>
                  <span>{display(profileData.birthDetails?.star)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Moon Sign</span>
                  <span>{display(profileData.birthDetails?.moonsign)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Gothra</span>
                  <span>{display(profileData.birthDetails?.gothra)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Pada</span>
                  <span>{display(profileData.birthDetails?.pada)}</span>
                </div>
              </div>
            </>
          )}

          {/* FAMILY DETAILS */}
          {activeTab === "family" && (
            <>
              <h3>Family Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Father Name</span>
                  <span>{display(profileData.familyDetails?.fatherName)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Father Alive</span>
                  <span>{display(profileData.familyDetails?.fatherAlive)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Father Occupation</span>
                  <span>
                    {display(profileData.familyDetails?.fatherOccupation)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Mother Name</span>
                  <span>{display(profileData.familyDetails?.motherName)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Mother Alive</span>
                  <span>{display(profileData.familyDetails?.motherAlive)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Mother Occupation</span>
                  <span>
                    {display(profileData.familyDetails?.motherOccupation)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>No. of Brothers</span>
                  <span>
                    {display(profileData.familyDetails?.noOfBrothers)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Married Brothers</span>
                  <span>
                    {display(profileData.familyDetails?.noOfBrothersMarried)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>No. of Sisters</span>
                  <span>{display(profileData.familyDetails?.noOfSisters)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Married Sisters</span>
                  <span>
                    {display(profileData.familyDetails?.noOfSistersMarried)}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* CONTACT DETAILS */}
          {activeTab === "contact" && (
            <>
              <h3>Contact Details</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Address 1</span>
                  <span>{display(profileData.contactDetails?.address1)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Address 2</span>
                  <span>{display(profileData.contactDetails?.address2)}</span>
                </div>
                <div className="my-detail-row">
                  <span>District</span>
                  <span>{display(profileData.contactDetails?.district)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Resident At</span>
                  <span>{display(profileData.contactDetails?.residentAt)}</span>
                </div>
              </div>
            </>
          )}

          {/* EDUCATION DETAILS */}
          {activeTab === "education" && (
            <>
              <h3>Education & Profession</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Education Stream</span>
                  <span>
                    {display(profileData.educationsDetails?.educationStream)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Qualification</span>
                  <span>
                    {display(profileData.educationsDetails?.qualification)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Occupation</span>
                  <span>
                    {display(profileData.educationsDetails?.occupation)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Working In</span>
                  <span>
                    {display(profileData.educationsDetails?.workingIn)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Annual Income</span>
                  <span>
                    {display(profileData.educationsDetails?.annualIncome)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Job Type</span>
                  <span>{display(profileData.educationsDetails?.jobType)}</span>
                </div>
              </div>
            </>
          )}

          {/* LOOKING FOR DETAILS */}
          {activeTab === "looking" && (
            <>
              <h3>Looking For</h3>
              <div className="my-details-list">
                <div className="my-detail-row">
                  <span>Age From</span>
                  <span>{display(profileData.lookingForDetails?.ageFrom)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Age To</span>
                  <span>{display(profileData.lookingForDetails?.ageTo)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Height</span>
                  <span>{display(profileData.lookingForDetails?.height)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Caste</span>
                  <span>{display(profileData.lookingForDetails?.caste)}</span>
                </div>
                <div className="my-detail-row">
                  <span>Subcaste</span>
                  <span>
                    {display(profileData.lookingForDetails?.subCaste)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Occupation</span>
                  <span>
                    {display(profileData.lookingForDetails?.occupation)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Annual Income</span>
                  <span>
                    {display(profileData.lookingForDetails?.annualIncome)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Marital Status</span>
                  <span>
                    {display(profileData.lookingForDetails?.maritalStatus)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Education Stream</span>
                  <span>
                    {display(profileData.lookingForDetails?.educationStream)}
                  </span>
                </div>
                <div className="my-detail-row">
                  <span>Qualification</span>
                  <span>
                    {display(profileData.lookingForDetails?.qualification)}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileInfo;
